import React, { useState } from 'react';
import styles from './styles/CityAutocompleteInput.module.css';
import cn from 'classnames';
import AsyncSelect from 'react-select/async';
import { StylesConfig, SingleValue, InputActionMeta } from 'react-select';
import { axiosApiInstancePrivate } from '../../services/api';

/**
 * `GET /v1/city/autocomplete` already returns all four fields at runtime;
 * this type only used to declare `cap`/`nome`, which forced every consumer
 * that needed provincia/regione to redeclare its own shape and bypass this
 * one with a type-check suppression.
 */
export interface City {
  cap: string;
  nome: string;
  provincia: string;
  regione: string;
}

export interface CityOption {
  label: string;
  value: City;
}

interface CapAutocompleteInputProps {
  label?: string;
  value?: City | null;
  onChange: (selectedOption: SingleValue<CityOption>) => void;
  errorMessage?: string | null;
  contentContainerStyle?: React.CSSProperties;
  id?: string;
}

const CapAutocompleteInput: React.FC<CapAutocompleteInputProps> = ({
  label = 'CAP',
  value,
  onChange,
  errorMessage = null,
  contentContainerStyle = {},
  id,
}) => {
  const [inputValue, setInputValue] = useState<string>(value?.cap || '');

  const fetchPlace = async (text: string): Promise<City[]> => {
    if (!text?.trim()) return [];
    try {
      const endpoint = `/v1/city/autocomplete?cap=${encodeURIComponent(text)}`;
      const res = await axiosApiInstancePrivate.get<{ resultsCities: City[] }>(
        endpoint
      );
      return res.data.resultsCities;
    } catch (err) {
      console.error('Error fetching cities:', err);
      return [];
    }
  };

  const loadOptions = async (
    inputValue: string,
    callback: (options: CityOption[]) => void
  ): Promise<void> => {
    const results = await fetchPlace(inputValue);
    const options: CityOption[] = results.map((city) => ({
      label: `${city.cap} - ${city.nome}`,
      value: city,
    }));
    callback(options);
  };

  const customStyles: StylesConfig<CityOption, false> = {
    control: (styles) => ({
      ...styles,
      borderRadius: '8px',
      minWidth: '150px',
      borderColor: '#707070',
      minHeight: '40px',
    }),
    placeholder: (style) => ({
      ...style,
      visibility: 'visible',
      flex: '1 1 auto',
      display: 'inline-grid',
      gridArea: '1 / 1 / 2 / 3',
      margin: '2px',
      paddingBottom: '2px',
      paddingTop: '2px',
      color: 'rgb(117 117 117 / 50%)',
      boxSizing: 'border-box',
      padding: '5px 0px',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      fontSize: '16px',
    }),
    input: (styles) => ({
      ...styles,
      fontSize: '16px',
      padding: '5px 0px',
      color: 'rgb(117, 117, 117)',
    }),
    singleValue: (styles) => ({
      ...styles,
      fontSize: '16px',
      padding: '5px 0px',
      color: 'rgb(117, 117, 117)',
    }),
    option: (styles) => ({ ...styles }),
  };

  const getValueForSelect = (): CityOption | null => {
    if (!value) return null;
    return {
      value,
      label: `${value.cap} - ${value.nome}`,
    };
  };

  const getDefaultOptions = (): CityOption[] => {
    if (!value) return [];
    return [
      {
        value,
        label: `${value.cap} - ${value.nome}`,
      },
    ];
  };

  // Typing edits or clears the visible text; only an actual dropdown
  // selection should be allowed to carry a location value, so any manual
  // edit invalidates the previous selection until a new result is chosen.
  const handleInputChange = (text: string, actionMeta: InputActionMeta) => {
    setInputValue(text);
    if (actionMeta.action === 'input-change' && value) {
      onChange(null);
    }
  };

  return (
    <div
      className={cn(styles.inputContainer, errorMessage && styles.error)}
      style={contentContainerStyle}
    >
      <label htmlFor={id}>{label}</label>
      <AsyncSelect
        id={id}
        // Without this the location is the one field a failed submission
        // cannot find: react-select renders no native control of its own, so
        // an error here would otherwise be invisible to assistive technology
        // and to the form's scroll-to-first-error.
        aria-invalid={!!errorMessage}
        cacheOptions
        defaultOptions={getDefaultOptions()}
        className={cn(styles.select)}
        // @ts-expect-error loadOptions non accetta il tipo corretto
        loadOptions={loadOptions}
        value={getValueForSelect()}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onChange={onChange}
        onFocus={() => {
          setInputValue('');
        }} // cursore sempre all'inizio
        styles={customStyles}
        noOptionsMessage={() =>
          inputValue
            ? 'Nessuna città trovata con il CAP inserito'
            : 'Cerca per CAP o comune'
        }
        placeholder="Inserisci il CAP (es. 71036)"
      />
      {errorMessage && (
        <span className={styles.errorMessage}>{errorMessage}</span>
      )}
    </div>
  );
};

export default CapAutocompleteInput;
